import React from "react";
import * as Icon from "react-feather";

import "./quickbooks-client-entry-modal.css";
import WComponent from "../../../../../common/WComponent";
import ToolsModal from "../../../kit/ToolsModal";
import BookkeepingService from "../../../../../../services/BookkeepingService";
import UtilityService from "../../../../../../services/UtilityService";

class QuickBooksClientEntryModal extends WComponent {
	state = { reference: "", lookedUp: "", plan: null, lookingUp: false, recording: false, error: "", recorded: null };

	onReferenceChange = event => this.update({ reference: event.target.value, plan: null, error: "" });

	onReferenceKeyDown = event => {
		if (event.key === "Enter") this.onLookUp();
	};

	onLookUp = async () => {
		const { clientId, wpfEngagementId, entry } = this.props;
		const reference = this.state.reference.trim();
		if (!reference) return;
		await this.update({ lookingUp: true, error: "", plan: null });
		const answer = await BookkeepingService.describeClientEntryToRecord({ clientId, wpfEngagementId, adjustingEntryHeaderId: entry.adjustingEntryHeaderId, qboJournalReference: reference });
		await this.update({ lookingUp: false, lookedUp: reference, plan: answer.plan || null, error: answer.error || "" });
	};

	onRecord = async () => {
		const { clientId, wpfEngagementId, entry } = this.props;
		await this.update({ recording: true, error: "" });
		const answer = await BookkeepingService.recordClientEntryInQuickBooks({ clientId, wpfEngagementId, adjustingEntryHeaderId: entry.adjustingEntryHeaderId, qboJournalReference: this.state.lookedUp });
		await this.update({ recording: false, error: answer.error || "", recorded: answer.recorded || null });
	};

	onDone = () => this.props.onClose({ recorded: !!this.state.recorded });

	renderJournal = () => {
		const { plan, lookedUp } = this.state;
		const journal = plan.journalInQuickBooks;
		if (!journal) return null;
		const howFound = BookkeepingService.describeHowTheJournalWasFound({ journalInQuickBooks: journal, typed: lookedUp });
		return (
			<div className="qbce__journal" data-test="qbo-client-entry-journal">
				{howFound && <div className="tk-notice tk-notice--warning" data-test="qbo-client-entry-found-by-id"><p className="tk-notice__detail">{howFound}</p></div>}
				<p className="tk-lead tk-lead--muted">
					Journal {journal.reference}, dated {UtilityService.formatDate({ value: journal.txnDate })}, entered {UtilityService.formatDateTime({ value: journal.createdAt })}.
					{journal.memo && <span className="qbce__memo"> Memo: {journal.memo}</span>}
				</p>
				<div className="qbce__table">
					<div className="tk-ledger__row tk-ledger__row--head qbce__line">
						<span>QuickBooks account</span>
						<span className="tk-ledger__amount">Debit</span>
						<span className="tk-ledger__amount">Credit</span>
					</div>
					{journal.lines.map((line, position) => (
						<div key={position} className="tk-ledger__row qbce__line">
							<span className="qbce__account">{line.accountName || `QuickBooks account ${line.qboAccountId}`}</span>
							<span className="tk-ledger__amount">{line.side === "DEBIT" ? UtilityService.formatCurrency(line.amount) : ""}</span>
							<span className="tk-ledger__amount">{line.side === "CREDIT" ? UtilityService.formatCurrency(line.amount) : ""}</span>
						</div>
					))}
					<div className="tk-ledger__row tk-ledger__row--total qbce__line">
						<span>Total</span>
						<span className="tk-ledger__amount">{UtilityService.formatCurrency(journal.total)}</span>
						<span className="tk-ledger__amount">{UtilityService.formatCurrency(journal.total)}</span>
					</div>
				</div>
			</div>
		);
	};

	renderPlan = () => {
		const { plan } = this.state;
		const { entry } = this.props;
		if (!plan) return null;
		const matchesFully = plan.comparison && plan.comparison.matches;
		return (
			<React.Fragment>
				{this.renderJournal()}
				{plan.refusals.length > 0 && (
					<div className="tk-notice tk-notice--danger" data-test="qbo-client-entry-refusals">
						{plan.refusals.map(refusal => <p key={refusal} className="tk-notice__detail">{refusal}</p>)}
					</div>
				)}
				{matchesFully && (
					<div className="tk-notice tk-notice--success" data-test="qbo-client-entry-matches">
						<p className="tk-notice__detail"><Icon.Check size={14} /> The journal's lines match adjusting entry {entry.entryNumber}{plan.comparison.accountsCompared ? "" : " on sides and amounts; the entry has a line not linked to a QuickBooks account, so the accounts could not be compared"}.</p>
					</div>
				)}
				{plan.canRecord && <p className="tk-lead tk-lead--muted" data-test="qbo-client-entry-effect">{BookkeepingService.describeWhatRecordingTheClientEntryDoes({ plan })}</p>}
			</React.Fragment>
		);
	};

	renderFooter = () => {
		const { recorded, recording, plan } = this.state;
		if (recorded) return <span className="tk-modal-actions"><button type="button" className="tk-button tk-button--primary" onClick={this.onDone}>Done</button></span>;
		const canRecord = !!(plan && plan.canRecord) && !recording;
		return (
			<span className="tk-modal-actions">
				<button type="button" className="tk-button tk-button--secondary" onClick={this.onDone} disabled={recording}>Cancel</button>
				<button type="button" className="tk-button tk-button--primary" data-test="qbo-client-entry-record" onClick={this.onRecord} disabled={!canRecord}>
					{recording ? "Recording…" : "Record it"}
				</button>
			</span>
		);
	};

	render() {
		const { entry } = this.props;
		const { reference, lookingUp, error, recorded } = this.state;
		return (
			<ToolsModal open onClose={this.onDone} title={`Adjusting entry ${entry.entryNumber} — entered by the client in QuickBooks`} width={640} footer={this.renderFooter()}>
				<div className="qbce" data-test="qbo-client-entry-modal">
					{recorded ? (
						<div className="tk-notice tk-notice--success" data-test="qbo-client-entry-recorded">
							<p className="tk-notice__detail"><Icon.CheckCircle size={14} /> {BookkeepingService.describeClientEntryRecording({ recorded, entryNumber: entry.entryNumber })}</p>
						</div>
					) : (
						<React.Fragment>
							<p className="tk-lead">
								Give the journal number QuickBooks shows for the client's entry (its Journal no.). It is read from QuickBooks and compared with the
								entry before anything is recorded; nothing is written to QuickBooks. Only a journal whose lines match the entry can be recorded — if the
								client entered different figures, change the entry here to match them first.
							</p>
							<div className="qbce__lookup">
								<input type="text" className="tk-input" value={reference} placeholder="Journal no." data-test="qbo-client-entry-reference" onChange={this.onReferenceChange} onKeyDown={this.onReferenceKeyDown} />
								<button type="button" className="tk-button tk-button--secondary" data-test="qbo-client-entry-lookup" onClick={this.onLookUp} disabled={lookingUp || !reference.trim()}>
									{lookingUp ? "Reading QuickBooks…" : "Look it up"}
								</button>
							</div>
							{error && <div className="tk-notice tk-notice--danger" data-test="qbo-client-entry-error"><p className="tk-notice__detail">{error}</p></div>}
							{this.renderPlan()}
						</React.Fragment>
					)}
				</div>
			</ToolsModal>
		);
	}
}

export default QuickBooksClientEntryModal;
